import {
    Dispatch,
    MutableRefObject,
    SetStateAction,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from "react";

function isElementNode(node: ChildNode): node is Element {
    return node.nodeType === 1;
}
/**
 * @description Calculates the max number of links can live in the global nav before having to render the 'show more' button
 *
 * It calculates the available width by comparing the distance between the 2 refs:
 * Left: <GlobalNavLinksContainer />
 * Right: <SearchAndProfile />
 *
 * It steps through all the children of the global nav links containers (children being the links).
 * On each loop it checks if the sum is greater than the availableWidth.
 *
 * If it is greater than the available width, then spit out the previous count as max children
 *
 * @param left Right element of global nav (<GlobalNavLinksContainer />)
 * @param right Right element of global nav (<SearchAndProfile />)
 * @param currentMaxChildren
 * @param buffer number of pixels to shrink available width by
 * @returns number of links that can be displayed in global nav bar
 */
// eslint-disable-next-line complexity
function calculateMaxChildren(
    left: MutableRefObject<Element | null>,
    right: MutableRefObject<Element | null>,
    currentMaxChildren: number,
    buffer: number
) {
    if (!left.current || !right.current) {
        return 0;
    }

    // with rewrite of global nav (JFIND-1024) it no longer uses gap but padding so this uses the default of 0px
    // if padding is changed so will the fallback number for it to accurately calculate maxChildren
    const unparsedGap = getComputedStyle(left.current).gap;
    const gap = unparsedGap.endsWith("px")
        ? parseInt(unparsedGap)
        : unparsedGap.endsWith("rem")
        ? parseInt(unparsedGap) * 16
        : 0;

    // remove the More/Less button
    const totalChildren = left.current.childNodes.length - 1;

    const { right: leftElRight, left: leftElLeft } =
        left.current.getBoundingClientRect();
    const { left: rightElLeft } = right.current.getBoundingClientRect();

    // the more/less element is always the last element child
    const moreOrLessElement = left.current.lastElementChild;
    // if the current max is less than the total possible children,
    // we're hiding some and showing More+
    const moreOrLessVisible = currentMaxChildren < totalChildren;
    // skip calculation if the screen is large enough to hold both
    // sections plus the buffer

    if (rightElLeft - leftElRight >= buffer && !moreOrLessVisible) {
        return totalChildren;
    }

    // how large can the UList be for the links
    const availableWidth = rightElLeft - leftElLeft - buffer;
    let currentMaxWidth = 0;
    let maxChildren = 0;

    for (const [index, node] of left.current.childNodes.entries()) {
        // skip non-element children; we shouldn't need this
        if (!isElementNode(node)) {
            continue;
        }

        // this is the More/Less button; don't try to place it, because we'll insert based on other elements
        if (index === left.current.childNodes.length - 1) {
            continue;
        }

        // skip last link element because we accounted for it in the previous element,
        // as there can't be fewer than two items in More menu
        if (index === left.current.childNodes.length - 2) {
            continue;
        }

        // look at the second-to-last link element, because More+ must always
        // include 2 or more items
        const isSecondToLastLinkElement =
            index === left.current.childNodes.length - 3;

        const lastTwoElementsWidth =
            node.clientWidth + (node.nextElementSibling?.clientWidth ?? 0);

        const nextWidth =
            currentMaxWidth +
            (isSecondToLastLinkElement
                ? lastTwoElementsWidth
                : node.clientWidth) +
            gap;

        if (
            maxChildren === 0 ||
            nextWidth +
                (!isSecondToLastLinkElement
                    ? moreOrLessElement?.clientWidth ?? 0
                    : 0) <
                availableWidth
        ) {
            currentMaxWidth = nextWidth;
            if (isSecondToLastLinkElement) {
                maxChildren += 2;
            } else {
                maxChildren += 1;
            }

            continue;
        } else {
            break;
        }
    }

    return maxChildren;
}

function useResizeObserver<E extends HTMLElement>(
    ref: MutableRefObject<E | null | undefined>
) {
    const [observer, setObserver] = useState<ResizeObserver>();
    const [rect, setRect] = useState<DOMRectReadOnly>();

    useEffect(() => {
        const obs = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            if (entries[0]) {
                setRect(entries[0].contentRect);
            }
        });
        setObserver(obs);
    }, [ref]);

    useEffect(() => {
        if (ref.current) {
            observer?.observe(ref.current);
        }

        return () => observer && observer.disconnect();
    }, [observer, ref]);

    return rect;
}

type UseMoreProps<L extends HTMLElement, R extends HTMLElement> = {
    leftElementRef: MutableRefObject<L | null>;
    rightElementRef: MutableRefObject<R | null>;
    buffer?: number;
    initialMaxItems?: number;
};

type UseMoreReturnType = {
    readonly maxItems: number;
    readonly shouldShowMoreItems: boolean;
    readonly showMoreItems: Dispatch<SetStateAction<boolean>>;
};

/**
 *
 * @param {UseMoreProps} props
 * @returns {UseMoreReturnType}
 *
 * @description Takes two MutableRefs containing HTML elements width minimum-acceptable widths (e.g. links in the global navigation bar),
 * compares the distance between them to a buffer, and determines whether the left element has enough width
 * to show all of its children without resizing.
 */
export function useMore<L extends HTMLElement, R extends HTMLElement>({
    leftElementRef,
    rightElementRef,
    buffer = 16,
    initialMaxItems = 0
}: UseMoreProps<L, R>): UseMoreReturnType {
    const [maxLinks, setMaxLinks] = useState(initialMaxItems);
    const [showMore, setShowMore] = useState(false);
    const parentElRef = useRef(leftElementRef.current?.parentElement);

    useEffect(() => {
        if (leftElementRef.current) {
            // This is the full left side that includes the logo, nav links and space before the search+profile component
            parentElRef.current =
                leftElementRef.current.closest?.("nav") ??
                leftElementRef.current.parentElement?.parentElement;
        }
    }, [leftElementRef]);

    const rect = useResizeObserver(parentElRef);

    useLayoutEffect(() => {
        if (rect?.right) {
            const maxChildren = calculateMaxChildren(
                leftElementRef,
                rightElementRef,
                maxLinks,
                buffer
            );
            setMaxLinks(maxChildren);
        }
    }, [maxLinks, leftElementRef, rightElementRef, rect?.right, buffer]);

    const output = useMemo(
        () => ({
            maxItems: maxLinks,
            shouldShowMoreItems: showMore,
            showMoreItems: setShowMore
        }),
        [showMore, maxLinks]
    );

    return output;
}
